import { useRef, type ReactNode, type KeyboardEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GiftIcon } from '@src/components/icons/GiftIcon';
import { CogIcon } from '@src/components/icons/CogIcon';
import { UserExcludeIcon } from '@src/components/icons/UserExcludeIcon';
import { useMobileBoxLayout } from './BoxLayouts';

const tabs = ['draw', 'settings', 'exclusions'] as const;
type DrawTab = typeof tabs[number];

const icons = { draw: GiftIcon, settings: CogIcon, exclusions: UserExcludeIcon };

export function OrganizerDrawTabs() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const active = tabs.find(tab => tab === params.get('drawTab')) ?? 'draw';
  function select(tab: DrawTab) {
    setParams(previous => {
      const next = new URLSearchParams(previous);
      next.set('tab', 'draw');
      if (tab === 'draw') next.delete('drawTab'); else next.set('drawTab', tab);
      return next;
    }, { preventScrollReset: true });
  }
  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const next = event.key === 'ArrowRight' ? (index + 1) % tabs.length
      : event.key === 'ArrowLeft' ? (index + tabs.length - 1) % tabs.length
      : event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : null;
    if (next === null) return;
    event.preventDefault(); refs.current[next]?.focus(); select(tabs[next]);
  }
  return (
    <div className="organizer-people__tabs organizer-draw__tabs" role="tablist" aria-label={t('game.draw.sectionTitle')}>
      {tabs.map((tab, index) => { const Icon = icons[tab]; return <button type="button" key={tab} role="tab"
        ref={node => { refs.current[index] = node; }}
        id={`draw-subtab-${tab}`} data-testid={`draw-subtab-${tab}`}
        aria-selected={active === tab} aria-controls={`draw-content-${tab}`} tabIndex={active === tab ? 0 : -1}
        onClick={() => select(tab)} onKeyDown={event => onKeyDown(event, index)}>
        <span className="organizer-people__tab-icon" aria-hidden="true"><Icon /></span>
        {t(tab === 'exclusions' ? 'game.draw.exclusionsLabel' : tab === 'draw' ? 'organizerPanel.drawShort' : 'organizerPanel.settings', { defaultValue: t(`organizerPanel.${tab}`) })}
      </button>; })}
    </div>
  );
}

export function OrganizerDrawContent({ draw, settings, exclusions, completed }: {
  draw: ReactNode; settings: ReactNode; exclusions: ReactNode; completed: boolean;
}) {
  const mobile = useMobileBoxLayout();
  const [params] = useSearchParams();
  const active = tabs.find(tab => tab === params.get('drawTab')) ?? 'draw';
  if (!mobile) return <div className="organizer-draw__layout">
    {completed ? <div className="organizer-draw__completed">{draw}</div> : draw}{settings}{exclusions}
  </div>;
  return (
    <section role="tabpanel" id={`draw-content-${active}`} aria-labelledby={`draw-subtab-${active}`} tabIndex={0}>
      {{ draw, settings, exclusions }[active]}
    </section>
  );
}
